<script lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { computed, provide } from 'vue';

import VChip from '../VChip/VChip.vue';
import VStyledHtml from '../VStyledHtml/VStyledHtml.vue';
import { type InfoBoxType } from './types';
import { typeInjectionKey } from './keys';

export interface VInfoBoxProps {
  type?: InfoBoxType;
  badgeText?: string;
  size?: 'small' | 'normal';
}

const { type, badgeText, size = 'normal' } = defineProps<VInfoBoxProps>();

const wrapperClasses = computed(() => {
  switch (type) {
    case 'info':
      return 'tw-border-info-300 tw-bg-info-200';
    case 'warning':
      return 'tw-border-orange-300 tw-bg-orange-200';
    case 'danger':
      return 'tw-border-red-100 tw-bg-red-50';
    default:
      return 'tw-border-grey-200 tw-bg-white';
  }
});

const tagColor = computed(() => {
  switch (type) {
    case 'info':
      return 'blue';
    case 'warning':
      return 'orange';
    case 'danger':
      return 'red';
    default:
      return undefined;
  }
});

const icon = computed(() => {
  switch (type) {
    case 'danger':
    case 'warning':
      return 'far fa-info-circle';
    case 'info':
    default:
      return 'far fa-info-circle';
  }
});

const sizeClasses: Record<typeof size, string> = {
  small: 'tw-px-3 tw-py-2',
  normal: 'tw-px-4 tw-py-6 md:tw-p-8 md:tw-pt-7',
};

provide(typeInjectionKey, { value: type });
</script>

<template>
  <div
    :aria-label="badgeText"
    class="tw-flex tw-rounded tw-border tw-border-solid"
    :class="{
      [sizeClasses[size]]: true,
      [wrapperClasses]: true,
      'tw-gap-2': !badgeText,
      'tw-flex-col tw-gap-4': badgeText,
    }"
    :role="type === 'info' ? 'status' : 'alert'"
  >
    <VChip v-if="badgeText" class="tw-self-start" :color="tagColor" :icon :label="badgeText" />

    <FontAwesomeIcon v-else class="tw-pt-1" :icon />

    <VStyledHtml class="tw-w-full tw-text-sm tw-text-grey-600">
      <slot></slot>
    </VStyledHtml>

    <ul v-if="$slots.actions" class="tw-grid tw-grow tw-gap-3 tw-p-0">
      <slot name="actions"></slot>
    </ul>
  </div>
</template>
